import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Zap, Save, Clock, Bell, Loader2, BookOpen, Info, Trash2, Search, X } from "lucide-react";
import { TAG_GROUPS, TAG_COUNT } from "@/lib/expertiseTags";
import PushBanner from "@/components/PushBanner";

const TOPIC_SUGGESTIONS = TAG_GROUPS;

const _FLAT_SUGGESTIONS = TAG_GROUPS.flatMap((g) => g.tags);
export { _FLAT_SUGGESTIONS };

const FREQ = [
  { v: "daily", l: { fr: "Tous les jours", en: "Daily" }, hint: { fr: "1 carte / matin", en: "1 card / morning" } },
  { v: "weekly", l: { fr: "Hebdomadaire", en: "Weekly" }, hint: { fr: "5 cartes le lundi", en: "5 cards on Monday" } },
  { v: "off", l: { fr: "Désactivé", en: "Off" }, hint: { fr: "Pas d'envoi auto", en: "No auto send" } },
];

const GUEST_DEFAULT_PREFS = {
  email_daily_lesson: true,
  email_weekly_digest: true,
  email_product_news: false,
  push_enabled: false,
  microlearning_topics: ["Bourgogne", "Champagne"],
  microlearning_frequency: "daily",
  microlearning_time: "08:00",
  microlearning_level: "amateur",
};

export default function Microlearning() {
  const { user } = useAuth();
  const { lang } = useT();
  const [prefs, setPrefs] = useState(user ? null : GUEST_DEFAULT_PREFS);
  const [busy, setBusy] = useState(false);
  const [flashcards, setFlashcards] = useState([]);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    if (user) {
      api.get("/me/notifications").then((r) => setPrefs(r.data)).catch(() => setPrefs(GUEST_DEFAULT_PREFS));
      api.get("/microlearning/flashcards").then((r) => setFlashcards(r.data?.flashcards || [])).catch(() => setFlashcards([]));
    } else {
      setPrefs(GUEST_DEFAULT_PREFS);
    }
  }, [user]);

  const deleteFlashcard = async (id) => {
    try {
      await api.delete(`/microlearning/flashcards/${id}`);
      setFlashcards((fs) => fs.filter((f) => f.id !== id));
      toast.success(lang === "fr" ? "Flashcard supprimée" : "Flashcard removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const save = async () => {
    if (!user) {
      toast.message(lang === "fr" ? "Créez un compte pour activer vos préférences." : "Create an account to activate your preferences.", { duration: 3500 });
      window.location.href = "/register?next=/microlearning";
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email_daily_lesson: prefs.email_daily_lesson,
        email_weekly_digest: prefs.email_weekly_digest,
        email_product_news: prefs.email_product_news,
        push_enabled: prefs.push_enabled,
        microlearning_topics: prefs.microlearning_topics,
        microlearning_frequency: prefs.microlearning_frequency,
        microlearning_time: prefs.microlearning_time,
        microlearning_level: prefs.microlearning_level,
      };
      await api.put("/me/notifications", payload);
      toast.success(lang === "fr" ? "Préférences enregistrées" : "Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const toggleTopic = (t) => {
    const list = prefs.microlearning_topics || [];
    setPrefs({ ...prefs, microlearning_topics: list.includes(t) ? list.filter((x) => x !== t) : [...list, t] });
  };

  if (!prefs) {
    return <div className="max-w-2xl mx-auto px-5 py-16 flex items-center gap-2 text-[#8B95A1]"><Loader2 className="w-5 h-5 animate-spin" /> {lang === "fr" ? "Chargement…" : "Loading…"}</div>;
  }

  const guest = !user;

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="microlearning-page">
      <span className="chip-pink inline-flex items-center gap-1.5"><Zap className="w-3 h-3" /> Microlearning{guest && <span className="ml-1 opacity-80">· {lang === "fr" ? "Mode invité" : "Guest mode"}</span>}</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
        {lang === "fr" ? "Apprendre en arrière-plan" : "Learning on autopilot"}
      </h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        {lang === "fr"
          ? "Quelques flashcards par jour ou par semaine, sur les sujets qui vous intéressent. geoVINUM AI vous les envoie au bon moment."
          : "A few flashcards a day or a week, on the topics you love. geoVINUM AI sends them at the right moment."}
      </p>

      {guest && (
        <div className="mt-5 rounded-2xl border border-[#E5276A]/25 bg-[#FFF8FB] p-4 flex items-start gap-3" data-testid="microlearning-guest-banner">
          <Info className="w-4 h-4 text-[#E5276A] shrink-0 mt-0.5" />
          <div className="text-sm text-[#1F3A5F] flex-1 min-w-0">
            <b>{lang === "fr" ? "Mode invité" : "Guest mode"}</b>{" "}
            {lang === "fr"
              ? "· Personnalisez librement vos préférences ci-dessous. Pour les enregistrer et recevoir vos flashcards, "
              : "· Try the preferences below freely. To save them and receive your flashcards, "}
            <Link to="/register?next=/microlearning" className="underline font-bold text-[#E5276A]" data-testid="microlearning-guest-register">
              {lang === "fr" ? "créez un compte gratuit" : "create a free account"}
            </Link>.
          </div>
        </div>
      )}

      <div className="mt-8 space-y-6">
        {!guest && <PushBanner />}
        {/* Topics */}
        <div className="app-card p-6" data-testid="microlearning-topics">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <BookOpen className="w-4 h-4 text-[#E5276A]" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{lang === "fr" ? "Sujets favoris" : "Favourite topics"}</div>
            <span className="ml-auto text-[11px] text-[#8B95A1]">
              {(prefs.microlearning_topics || []).length} / {TAG_COUNT}
            </span>
          </div>
          <p className="text-xs text-[#8B95A1] mb-3">
            {lang === "fr"
              ? "Régions du monde, cépages, spiritueux, chaîne de valeur… choisissez tout ce qui vous intéresse. geoVINUM AI alterne intelligemment."
              : "World regions, grape varieties, spirits, value chain… pick everything that interests you. geoVINUM AI rotates smartly."}
          </p>
          <TopicPicker
            selected={prefs.microlearning_topics || []}
            onToggle={toggleTopic}
            lang={lang}
          />
        </div>

        {/* Frequency */}
        <div className="app-card p-6" data-testid="microlearning-frequency">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-[#E5276A]" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{lang === "fr" ? "Fréquence" : "Frequency"}</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {FREQ.map((f) => {
              const active = prefs.microlearning_frequency === f.v;
              return (
                <button
                  key={f.v}
                  onClick={() => setPrefs({ ...prefs, microlearning_frequency: f.v })}
                  data-testid={`microlearning-freq-${f.v}`}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    active ? "border-[#E5276A] bg-[#FCE4ED]" : "border-[#1F3A5F]/15 bg-white hover:border-[#E5276A]"
                  }`}
                >
                  <div className={`font-bold ${active ? "text-[#E5276A]" : "text-[#1F3A5F]"}`}>{f.l[lang] || f.l.fr}</div>
                  <div className="text-xs text-[#8B95A1] mt-0.5">{f.hint[lang] || f.hint.fr}</div>
                </button>
              );
            })}
          </div>
          {prefs.microlearning_frequency !== "off" && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <label className="text-xs text-[#4A5568]">{lang === "fr" ? "Heure d'envoi" : "Send at"}</label>
              <input
                type="time"
                value={prefs.microlearning_time || "08:00"}
                onChange={(e) => setPrefs({ ...prefs, microlearning_time: e.target.value })}
                data-testid="microlearning-time"
                className="bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-3 py-1.5 text-sm text-[#1F3A5F]"
              />
              <label className="text-xs text-[#4A5568] ml-2">{lang === "fr" ? "Niveau" : "Level"}</label>
              <select
                value={prefs.microlearning_level || "amateur"}
                onChange={(e) => setPrefs({ ...prefs, microlearning_level: e.target.value })}
                data-testid="microlearning-level"
                className="bg-white border border-[#1F3A5F]/15 rounded-lg px-3 py-1.5 text-sm text-[#1F3A5F]"
              >
                <option value="debutant">{lang === "fr" ? "Découverte" : "Beginner"}</option>
                <option value="amateur">Amateur</option>
                <option value="pro">Pro</option>
                <option value="expert">Expert</option>
              </select>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="app-card p-6" data-testid="microlearning-notifications">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-[#E5276A]" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{lang === "fr" ? "Notifications" : "Notifications"}</div>
          </div>
          <div className="space-y-2.5">
            {[
              { k: "email_daily_lesson", l: { fr: "Email leçon quotidienne", en: "Daily lesson email" } },
              { k: "email_weekly_digest", l: { fr: "Récap hebdomadaire", en: "Weekly digest" } },
              { k: "email_product_news", l: { fr: "Actus produit & nouveautés", en: "Product news" } },
              { k: "push_enabled", l: { fr: "Notifications push (PWA)", en: "Push notifications (PWA)" } },
            ].map(({ k, l }) => (
              <label key={k} className="flex items-center justify-between p-3 rounded-lg hover:bg-[#FBF7F3] cursor-pointer">
                <span className="text-sm text-[#1F3A5F]">{l[lang] || l.fr}</span>
                <input
                  type="checkbox"
                  checked={!!prefs[k]}
                  onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })}
                  data-testid={`microlearning-notif-${k}`}
                  className="w-5 h-5 rounded accent-[#E5276A]"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-[#1F3A5F]/8 gap-3 flex-wrap">
          <p className="text-xs text-[#8B95A1] italic">
            {guest
              ? (lang === "fr" ? "Mode invité : créez un compte pour enregistrer vos préférences." : "Guest mode: create an account to save your preferences.")
              : (lang === "fr" ? "Vos préférences sont enregistrées sur votre compte." : "Your prefs are saved on your account.")}
          </p>
          <button onClick={save} disabled={busy} data-testid="microlearning-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {guest
              ? (lang === "fr" ? "Créer un compte pour enregistrer" : "Sign up to save")
              : (lang === "fr" ? "Enregistrer" : "Save")}
          </button>
        </div>

        {/* Personal flashcards from missed quiz answers, articles, etc. */}
        {!guest && flashcards.length > 0 && (
          <div className="app-card p-6" data-testid="microlearning-flashcards">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-[#E5276A]" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">
                {lang === "fr" ? `Mes ${flashcards.length} flashcards de révision` : `My ${flashcards.length} review flashcards`}
              </div>
              <a href="/microlearning/review" data-testid="microlearning-review-cta"
                className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#E5276A] text-white text-[11px] font-bold hover:bg-[#C01661]">
                🎯 {lang === "fr" ? "Réviser maintenant" : "Review now"}
              </a>
            </div>
            <p className="text-xs text-[#8B95A1] mb-4">
              {lang === "fr"
                ? "Chaque erreur de quiz est ajoutée ici pour être révisée à intervalles réguliers (algorithme SM-2 : espacement croissant)."
                : "Every quiz miss lands here and is scheduled for spaced repetition (SM-2 algorithm)."}
            </p>
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {flashcards.map((f) => (
                <div key={f.id} data-testid={`microlearning-flashcard-${f.id}`}
                  className="border border-[#1F3A5F]/12 rounded-xl overflow-hidden hover:border-[#E5276A] transition-colors">
                  <button
                    onClick={() => setRevealed((r) => ({ ...r, [f.id]: !r[f.id] }))}
                    className="w-full text-left px-4 py-3 hover:bg-[#FBF7F3] transition-colors"
                  >
                    <div className="text-sm font-bold text-[#1F3A5F]">{f.question}</div>
                    {revealed[f.id] && (
                      <>
                        <div className="mt-2 text-sm text-[#22C55E] font-bold">→ {f.answer}</div>
                        {f.explanation && (
                          <p className="mt-2 text-xs italic text-[#4A5568]">{f.explanation}</p>
                        )}
                      </>
                    )}
                    {!revealed[f.id] && (
                      <div className="mt-1 text-[10px] text-[#8B95A1] uppercase tracking-wider">
                        {lang === "fr" ? "Cliquez pour voir la réponse" : "Click to reveal"}
                      </div>
                    )}
                  </button>
                  <div className="border-t border-[#1F3A5F]/8 px-4 py-2 flex items-center justify-between bg-[#FBF7F3]/40">
                    <span className="text-[10px] text-[#8B95A1] uppercase tracking-wider inline-flex items-center gap-1">
                      {f.source?.startsWith("academy:") ? (
                        <><span className="text-[#E5276A]">🎓</span> {f.source_label || `Academy · ${f.source.replace("academy:","")}`}</>
                      ) : f.source?.startsWith("deck:") ? (
                        <><span>📚</span> {f.source_label || f.source.replace("deck:","Deck · ")}</>
                      ) : f.source === "quiz_review" ? (
                        lang === "fr" ? "Depuis un quiz" : "From a quiz"
                      ) : (
                        f.source_label || f.source
                      )}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteFlashcard(f.id); }}
                      className="p-1 rounded text-[#8B95A1] hover:text-[#E5276A]"
                      title="Supprimer"
                      data-testid={`microlearning-flashcard-delete-${f.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/** Searchable, grouped, multi-select tag picker used for topic preferences. */
function TopicPicker({ selected, onToggle, lang }) {
  const [q, setQ] = React.useState("");
  const [openGroup, setOpenGroup] = React.useState(null);
  const nq = q.trim().toLowerCase();

  const groups = TAG_GROUPS.map((g) => ({
    ...g,
    filtered: nq ? g.tags.filter((t) => t.toLowerCase().includes(nq)) : g.tags,
  })).filter((g) => g.filtered.length > 0);

  return (
    <div className="space-y-3" data-testid="microlearning-topic-picker">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-[#8B95A1] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="microlearning-topic-search"
          placeholder={lang === "fr" ? "Rechercher un thème (Rioja, Islay, biodynamie…)" : "Search topics (Rioja, Islay, biodynamics…)"}
          className="w-full pl-9 pr-9 py-2 bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] rounded-full text-sm text-[#1F3A5F] outline-none"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B95A1] hover:text-[#E5276A]">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {selected.length > 0 && (
        <div className="p-3 rounded-xl bg-[#FCE4ED]/40 border border-[#E5276A]/20">
          <div className="text-[10px] uppercase tracking-wider text-[#E5276A] font-bold mb-2">
            {lang === "fr" ? "Sélectionnés" : "Selected"} ({selected.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selected.map((t) => (
              <button
                key={t}
                onClick={() => onToggle(t)}
                data-testid={`microlearning-topic-selected-${t.toLowerCase().replace(/[^a-z0-9]/gi, "-").slice(0, 30)}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-[#E5276A] text-white hover:bg-[#C01661]"
              >
                {t} <X className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {groups.map((g) => {
          const isOpen = openGroup === g.id || nq !== "";
          const groupSelCount = g.filtered.filter((t) => selected.includes(t)).length;
          return (
            <div key={g.id} className="border border-[#1F3A5F]/12 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenGroup(isOpen && !nq ? null : g.id)}
                data-testid={`microlearning-topic-group-${g.id}`}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[#FBF7F3] transition-colors"
              >
                <span className="font-bold text-sm text-[#1F3A5F] flex-1">{g.label}</span>
                {groupSelCount > 0 && (
                  <span className="text-[10px] font-bold text-white bg-[#E5276A] rounded-full px-2 py-0.5">
                    {groupSelCount}
                  </span>
                )}
                <span className="text-[11px] text-[#8B95A1]">{g.filtered.length}</span>
                <span className="text-[#8B95A1]">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 pt-1 flex flex-wrap gap-1.5">
                  {g.filtered.map((t) => {
                    const active = selected.includes(t);
                    return (
                      <button
                        key={t}
                        onClick={() => onToggle(t)}
                        data-testid={`microlearning-topic-${t.toLowerCase().replace(/[^a-z0-9]/gi, "-").slice(0, 30)}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${
                          active
                            ? "bg-[#E5276A] text-white border-[#E5276A]"
                            : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

